import React, { useState } from "react";
import api from "../../services/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX = 10;

/**
 * The addresses that hear about support.
 *
 * A single "contact email" field was the wrong shape for this: support is a
 * couple of people, or a shared inbox plus somebody's phone, and the moment it
 * is one box it is one box that fills up while its owner is on holiday. So the
 * field holds a list — added one at a time, visible as chips, removable.
 *
 * The test button exists because this is the one setting whose failure is
 * completely silent: a wrong address, or a Brevo key that stopped working,
 * looks exactly like "no tickets today".
 */
export default function SupportEmailsField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const add = (raw: string) => {
    // Pasting a whole list should work, not be rejected as "one bad address".
    const parts = raw
      .split(/[,;\s]+/)
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length === 0) return;

    const bad = parts.find((p) => !EMAIL_RE.test(p));
    if (bad) {
      setError(`„${bad}" nu este o adresă de email validă.`);
      return;
    }

    const next = [...value];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    if (next.length > MAX) {
      setError(`Maxim ${MAX} adrese.`);
      return;
    }

    onChange(next);
    setDraft("");
    setError(null);
  };

  const remove = (email: string) => {
    onChange(value.filter((e) => e !== email));
    setError(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Test what is on screen, not what was last saved — otherwise the button
      // checks an address the admin just replaced.
      const { data } = await api.post<{ sent: string[]; failed: string[] }>(
        "/platform-settings/support-emails/test",
        value.length > 0 ? { emails: value } : {}
      );
      const sent = data.sent || [];
      const failed = data.failed || [];
      setTestResult({
        ok: failed.length === 0,
        message: failed.length
          ? `Trimis către ${sent.length}. Eșuat: ${failed.join(", ")}`
          : `Email de test trimis către ${sent.join(", ")}. Verifică inbox-ul (și Spam).`,
      });
    } catch (e: any) {
      setTestResult({
        ok: false,
        message: e?.response?.data?.error || "Nu am putut trimite emailul de test.",
      });
    }
    setTesting(false);
  };

  return (
    <div className="p-5 border bg-slate-50 border-slate-200 rounded-xl">
      <label className="block mb-1 text-sm font-medium text-slate-700">
        Notificări suport
      </label>
      <p className="mb-3 text-sm text-slate-500">
        Aici ajung tichetele de suport noi și anunțurile despre cluburile care își schimbă planul.
        Dacă lista e goală, notificările merg la adresele cu care se autentifică administratorii de platformă.
      </p>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {value.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white border rounded-lg border-slate-300 text-slate-700"
            >
              {email}
              <button
                type="button"
                onClick={() => remove(email)}
                aria-label={`Elimină ${email}`}
                className="transition-colors text-slate-400 hover:text-rose-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          inputMode="email"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => draft.trim() && add(draft)}
          placeholder="suport@clubul-tau.ro"
          aria-label="Adaugă adresă de email pentru notificări"
          className="flex-1 px-4 py-3 transition-all bg-white border border-slate-300 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
        />
        <button
          type="button"
          onClick={() => add(draft)}
          disabled={!draft.trim()}
          className="px-4 py-3 text-sm font-semibold text-white transition-colors bg-violet-600 rounded-xl hover:bg-violet-700 disabled:opacity-40"
        >
          Adaugă
        </button>
      </div>

      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button
          type="button"
          onClick={sendTest}
          disabled={testing}
          className="px-4 py-2 text-sm font-semibold transition-colors bg-white border text-slate-700 border-slate-300 rounded-xl hover:bg-slate-100 disabled:opacity-50"
        >
          {testing ? "Se trimite..." : "Trimite email de test"}
        </button>
        <span className="text-xs text-slate-400">
          Nu e nevoie să salvezi întâi — testul folosește adresele de mai sus.
        </span>
      </div>

      {testResult && (
        <p className={`mt-2 text-sm ${testResult.ok ? "text-emerald-600" : "text-rose-600"}`}>
          {testResult.message}
        </p>
      )}
    </div>
  );
}
